import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { CandidateRole } from "src/app/models/enum";
import {
	AssessmentConfigRes,
	ManeuverOutcomeDraft,
	ManeuverOutcomeValue,
	OBComments,
	OBCommentsReq,
	ResetManeuverItem,
	TemCompetencyOutcomeDraft,
} from "src/app/models/models";
import { buildTs1V3CandidateSummaryComments } from "./ts1-rating-ob-interaction";
import {
	isOutcomeSlotEnabled,
	outcomeCommentField,
	outcomeRatingRequiresComment,
	OutcomeSlot,
} from "./ts1-rating-outcome-interaction";
import {
	buildTs1ReviewCompetencyBlocks,
	isCompetencyManeuverCode,
	step3PfColumnOutcomeSlot,
	step3PmColumnOutcomeSlot,
	Ts1ReviewCompetencyBlock,
} from "./ts1-rating-review.utils";
import { formatTs1SummaryManeuverLabel } from "./ts1-summary-display.utils";
import { temCompetencySlotForCandidateIndex } from "./ts1-tem-competency-slots.utils";

export interface Step3MissingObComment {
	candidateIndex: number;
	candidateName: string;
	obLabel: string;
	maneuverLabel: string;
	candidateRole?: CandidateRole;
	/** `true` = added, `false` = removed */
	action: boolean;
}

export type Step3ReviewGapSection = "outcome" | "competency" | "maneuver" | "reminderNotes";

/** Scroll / highlight target for a Step 3 review gap. */
export interface Step3ReviewIncompleteItem {
	candidateIndex: number;
	candidateName: string;
	section: Step3ReviewGapSection;
	/** Field key within the section (e.g. sessionCheck, KNO, TO:pf). */
	field?: string;
	/** Short label without candidate prefix. */
	label: string;
	/** Full reason string (matches legacy getStep3CoreReviewIncompleteReasons output). */
	reason: string;
}

export interface Step3ReviewIncompleteGroup {
	candidateIndex: number;
	candidateName: string;
	items: Step3ReviewIncompleteItem[];
}

export interface Step3ReviewValidationParams {
	candidateCount: number;
	crewRoles: CandidateRole[];
	assessmentConfig: AssessmentConfigRes[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[];
	signOffForm: UntypedFormArray | null | undefined;
	getCompetencyBlocksForIndex: (candidateIndex: number) => Ts1ReviewCompetencyBlock[];
	/** Optional — needed for OB comment validation at Sign off. */
	ratingForm?: UntypedFormArray | null;
	getCandidateNameForIndex?: (index: number) => string;
	getCandidateIdForIndex?: (index: number) => string;
	selectedOBs?: OBComments[];
	addRemoveOb?: OBCommentsReq[];
	resetManeuvers?: ResetManeuverItem[];
}

/** Step 2 → Review: maneuver outcome completeness only (no recommendation / Rating C / OB comments). */
export interface Step2ManeuverOutcomesValidationParams {
	candidateCount: number;
	crewRoles: CandidateRole[];
	assessmentConfig: AssessmentConfigRes[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
}

function isOutcomeValueComplete(
	outcome: ManeuverOutcomeDraft | TemCompetencyOutcomeDraft | undefined,
	slot: OutcomeSlot,
): boolean {
	if (!outcome) return false;

	const value = outcome[slot as keyof typeof outcome] as ManeuverOutcomeValue | undefined;
	if (value == null) return false;
	if (value === "na" || value === "inc") return true;
	if (typeof value !== "number" || value < 1 || value > 5) return false;

	if (outcomeRatingRequiresComment(value)) {
		const commentKey = outcomeCommentField(slot);
		const comment = outcome[commentKey as keyof typeof outcome];
		return typeof comment === "string" && comment.trim().length > 0;
	}

	return true;
}

function isRecommendationComplete(signOffGroup: UntypedFormGroup | null | undefined): boolean {
	if (!signOffGroup) return false;

	const category = signOffGroup.get("category")?.value;
	if (category !== "Training" && category !== "Check") return false;

	const sessionCheck = signOffGroup.get("sessionCheck")?.value;
	if (sessionCheck === null || sessionCheck === undefined) return false;

	const objectiveCheck = signOffGroup.get("objectiveCheck")?.value;
	if (objectiveCheck === null || objectiveCheck === undefined) return false;

	const clearanceCheck = signOffGroup.get("clearanceCheck")?.value;
	if (clearanceCheck === null || clearanceCheck === undefined) return false;

	const finalRating = signOffGroup.get("finalRating")?.value;
	if (typeof finalRating !== "number" || finalRating < 1 || finalRating > 5) return false;

	return true;
}

function temCompetencySlot(candidateIndex: number, hasSecondCandidate: boolean): OutcomeSlot {
	return temCompetencySlotForCandidateIndex(candidateIndex, hasSecondCandidate);
}

function isCompetencyRatingComplete(params: {
	candidateIndex: number;
	hasSecondCandidate: boolean;
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[];
	competencyBlocks: Ts1ReviewCompetencyBlock[];
}): boolean {
	const { candidateIndex, hasSecondCandidate, temCompetencyOutcomes, competencyBlocks } = params;
	const slot = temCompetencySlot(candidateIndex, hasSecondCandidate);

	for (const block of competencyBlocks) {
		if (block.notGraded) continue;
		if (block.ratingC == null) return false;

		const outcome = temCompetencyOutcomes.find((o) => o.competencyCode === block.code);
		if (!isOutcomeValueComplete(outcome, slot)) return false;
	}

	return true;
}

function isManeuverRatingsComplete(params: {
	candidateIndex: number;
	crewRoles: CandidateRole[];
	hasSecondCandidate: boolean;
	assessmentConfig: AssessmentConfigRes[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
}): boolean {
	return (
		maneuverIncompleteReasons({
			...params,
			requireBothColumns: true,
		}).length === 0
	);
}

export function isStep3CandidateReviewComplete(candidateIndex: number, params: Step3ReviewValidationParams): boolean {
	const hasSecondCandidate = params.candidateCount >= 2;
	const signOffGroup = params.signOffForm?.at(candidateIndex) as UntypedFormGroup | undefined;

	if (!isRecommendationComplete(signOffGroup)) return false;

	const competencyBlocks = params.getCompetencyBlocksForIndex(candidateIndex);
	if (
		!isCompetencyRatingComplete({
			candidateIndex,
			hasSecondCandidate,
			temCompetencyOutcomes: params.temCompetencyOutcomes,
			competencyBlocks,
		})
	) {
		return false;
	}

	if (
		!isManeuverRatingsComplete({
			candidateIndex,
			crewRoles: params.crewRoles,
			hasSecondCandidate,
			assessmentConfig: params.assessmentConfig,
			maneuverOutcomes: params.maneuverOutcomes,
		})
	) {
		return false;
	}

	const candidateName = candidateLabel(candidateIndex);
	if (reminderStatusIncompleteItems(signOffGroup, candidateIndex, candidateName).length > 0) {
		return false;
	}

	return true;
}

/** True when an OB +/- row requires an instructor comment that is still empty.
 * Includes pre-repeat (`isRepeat`) and pre–maneuver-N/A empty add/remove rows.
 * Skips undo/N/A-toggle markers and outcome/observation notes.
 */
export function isStep3ObRowMissingRequiredComment(row: OBComments, _addRemoveOb: OBCommentsReq[] = []): boolean {
	if (row.action == null || row.ob === "TEM" || row.ob === "REPEAT") return false;
	// Undo markers and explicit OB N/A toggles (action null already handled) — not comment gaps.
	if (row.isReset || row.lastActionDisabled) return false;
	if (row.isManeuverOutcome || row.isTemCompetencyOutcome || row.isObservationComment) return false;
	// Cell-level N/A summary rows are stored with action null; if a disabled add/remove
	// still carries action +/- and empty text, treat it as a missing comment too.
	return !String(row.instructorComment ?? "").trim();
}

/** True when a summary OB +/- row is editable and still missing its required instructor comment. */
export function isTs1SummaryObCommentMissing(comment: OBComments | null | undefined): boolean {
	if (!comment || comment.isDisabled) return false;
	return isStep3ObRowMissingRequiredComment(comment);
}

function missingObDedupeKey(row: OBComments): string {
	return `${row.candidateId ?? ""}::${row.maneuverCode ?? ""}::${row.observationCode ?? row.ob ?? ""}::${row.candidateRole ?? ""}::${row.action}::${row.createdAt ?? ""}`;
}

function collectCandidateObRowsForCommentCheck(params: {
	candidateId: string;
	selectedOBs: OBComments[];
	ratingForm?: UntypedFormArray | null;
	crewRoles: CandidateRole[];
	addRemoveOb: OBCommentsReq[];
	resetManeuvers: ResetManeuverItem[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
}): OBComments[] {
	const { candidateId, selectedOBs, ratingForm, crewRoles, addRemoveOb, resetManeuvers, maneuverOutcomes } = params;

	// Draft selectedOBs is authoritative for every add/remove (including empty + remove pairs).
	const fromDraft = selectedOBs.filter((row) => row.candidateId === candidateId);

	if (!ratingForm?.length) return fromDraft;

	// Union live summary rows so in-memory form OBs not yet flushed to draft are not missed.
	const fromSummary = buildTs1V3CandidateSummaryComments({
		ratingForm,
		draftSelected: selectedOBs,
		crewRoles,
		addRemoveOb,
		resetManeuvers,
		maneuverOutcomes,
		candidateId,
	});

	const seen = new Set<string>();
	const merged: OBComments[] = [];
	for (const row of [...fromDraft, ...fromSummary]) {
		const key = missingObDedupeKey(row);
		if (seen.has(key)) continue;
		seen.add(key);
		merged.push(row);
	}
	return merged;
}

/** OB +/- rows missing instructor comments, grouped per candidate for the Sign-off popup. */
export function getStep3MissingObComments(params: Step3ReviewValidationParams): Step3MissingObComment[] {
	if (!params.candidateCount || !params.getCandidateIdForIndex) return [];

	const selectedOBs = params.selectedOBs ?? [];
	const addRemoveOb = params.addRemoveOb ?? [];
	const resetManeuvers = params.resetManeuvers ?? [];
	const missing: Step3MissingObComment[] = [];

	for (let i = 0; i < params.candidateCount; i++) {
		const candidateId = params.getCandidateIdForIndex(i);
		if (!candidateId) continue;

		const rows = collectCandidateObRowsForCommentCheck({
			candidateId,
			selectedOBs,
			ratingForm: params.ratingForm,
			crewRoles: params.crewRoles,
			addRemoveOb,
			resetManeuvers,
			maneuverOutcomes: params.maneuverOutcomes,
		});

		const seen = new Set<string>();
		const candidateName = params.getCandidateNameForIndex?.(i) ?? `Candidate ${i + 1}`;

		for (const row of rows) {
			if (!isStep3ObRowMissingRequiredComment(row, addRemoveOb)) continue;

			const key = missingObDedupeKey(row);
			if (seen.has(key)) continue;
			seen.add(key);

			missing.push({
				candidateIndex: i,
				candidateName,
				obLabel: row.ob ?? "",
				maneuverLabel: formatTs1SummaryManeuverLabel(row, resetManeuvers),
				candidateRole: row.candidateRole,
				action: row.action === true,
			});
		}
	}

	return missing;
}

export function isStep3ObCommentsComplete(params: Step3ReviewValidationParams): boolean {
	return getStep3MissingObComments(params).length === 0;
}

function reminderStatusIncompleteItems(
	group: UntypedFormGroup | undefined,
	candidateIndex: number,
	candidateName: string,
): Step3ReviewIncompleteItem[] {
	const responses = group?.get("reminderNoteResponses");
	if (!(responses instanceof UntypedFormArray) || !responses.length) return [];
	const firstMissing = responses.controls.find(
		(ctrl) => ctrl.get("requireStatus")?.value && !ctrl.get("status")?.value,
	);
	if (!firstMissing) return [];

	const noteId = String(firstMissing.get("noteId")?.value ?? "").trim();
	const label = "Select a status for each required reminder note";
	return [
		{
			candidateIndex,
			candidateName,
			section: "reminderNotes",
			field: noteId || "reminderStatus",
			label,
			reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
		},
	];
}

function candidateLabel(candidateIndex: number): string {
	return `Candidate ${candidateIndex + 1}`;
}

function recommendationIncompleteItems(
	signOffGroup: UntypedFormGroup | undefined,
	candidateIndex: number,
	candidateName: string,
): Step3ReviewIncompleteItem[] {
	if (!signOffGroup) {
		const label = "Complete the Final Training/Check Session Outcome section";
		return [
			{
				candidateIndex,
				candidateName,
				section: "outcome",
				field: "outcome",
				label,
				reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
			},
		];
	}

	const items: Step3ReviewIncompleteItem[] = [];
	const category = signOffGroup.get("category")?.value;
	if (category !== "Training" && category !== "Check") {
		const label = "Select Training or Check";
		items.push({
			candidateIndex,
			candidateName,
			section: "outcome",
			field: "category",
			label,
			reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
		});
	}

	const sessionCheck = signOffGroup.get("sessionCheck")?.value;
	if (sessionCheck === null || sessionCheck === undefined) {
		const label = "Answer whether the session was completed";
		items.push({
			candidateIndex,
			candidateName,
			section: "outcome",
			field: "sessionCheck",
			label,
			reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
		});
	}

	const objectiveCheck = signOffGroup.get("objectiveCheck")?.value;
	if (objectiveCheck === null || objectiveCheck === undefined) {
		const label = "Answer whether objectives are met";
		items.push({
			candidateIndex,
			candidateName,
			section: "outcome",
			field: "objectiveCheck",
			label,
			reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
		});
	}

	const clearanceCheck = signOffGroup.get("clearanceCheck")?.value;
	if (clearanceCheck === null || clearanceCheck === undefined) {
		const label = "Answer Cleared for Check";
		items.push({
			candidateIndex,
			candidateName,
			section: "outcome",
			field: "clearanceCheck",
			label,
			reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
		});
	}

	const finalRating = signOffGroup.get("finalRating")?.value;
	if (typeof finalRating !== "number" || finalRating < 1 || finalRating > 5) {
		const label = "Select Overall Grading (1–5)";
		items.push({
			candidateIndex,
			candidateName,
			section: "outcome",
			field: "finalRating",
			label,
			reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
		});
	}

	return items;
}

function competencyIncompleteItems(params: {
	candidateIndex: number;
	candidateName: string;
	hasSecondCandidate: boolean;
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[];
	competencyBlocks: Ts1ReviewCompetencyBlock[];
}): Step3ReviewIncompleteItem[] {
	const { candidateIndex, candidateName, hasSecondCandidate, temCompetencyOutcomes, competencyBlocks } = params;
	const slot = temCompetencySlot(candidateIndex, hasSecondCandidate);

	const items: Step3ReviewIncompleteItem[] = [];
	for (const block of competencyBlocks) {
		if (block.notGraded) continue;

		const outcome = temCompetencyOutcomes.find((o) => o.competencyCode === block.code);
		if (block.ratingC == null || !isOutcomeValueComplete(outcome, slot)) {
			const label = `Set Rating C for ${block.code}`;
			items.push({
				candidateIndex,
				candidateName,
				section: "competency",
				field: block.code,
				label,
				reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
			});
		}
	}
	return items;
}

function maneuverIncompleteItems(params: {
	candidateIndex: number;
	candidateName: string;
	crewRoles: CandidateRole[];
	hasSecondCandidate: boolean;
	assessmentConfig: AssessmentConfigRes[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
	/** Step 3 shows PF and PM for the viewing candidate; both columns are required. */
	requireBothColumns?: boolean;
}): Step3ReviewIncompleteItem[] {
	const {
		candidateIndex,
		candidateName,
		crewRoles,
		hasSecondCandidate,
		assessmentConfig,
		maneuverOutcomes,
		requireBothColumns = false,
	} = params;
	const pfSlot = step3PfColumnOutcomeSlot(candidateIndex);
	const pmSlot = step3PmColumnOutcomeSlot(candidateIndex, hasSecondCandidate);
	const items: Step3ReviewIncompleteItem[] = [];
	const pfRequired = requireBothColumns || isOutcomeSlotEnabled(pfSlot, crewRoles, hasSecondCandidate);
	const pmRequired = requireBothColumns || isOutcomeSlotEnabled(pmSlot, crewRoles, hasSecondCandidate);

	for (const assessment of assessmentConfig) {
		const maneuverCode = assessment.maneuverType.code;
		if (isCompetencyManeuverCode(maneuverCode)) continue;
		const maneuverLabel = assessment.maneuverType.description ?? maneuverCode;
		const outcome = maneuverOutcomes.find((o) => o.maneuverCode === maneuverCode);

		if (pfRequired && !isOutcomeValueComplete(outcome, pfSlot)) {
			const label = `Rate PF for ${maneuverLabel}`;
			items.push({
				candidateIndex,
				candidateName,
				section: "maneuver",
				field: `${maneuverCode}:pf`,
				label,
				reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
			});
		}

		if (pmRequired && !isOutcomeValueComplete(outcome, pmSlot)) {
			const label = `Rate PM for ${maneuverLabel}`;
			items.push({
				candidateIndex,
				candidateName,
				section: "maneuver",
				field: `${maneuverCode}:pm`,
				label,
				reason: `${candidateName}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`,
			});
		}
	}

	return items;
}

function maneuverIncompleteReasons(params: {
	candidateIndex: number;
	crewRoles: CandidateRole[];
	hasSecondCandidate: boolean;
	assessmentConfig: AssessmentConfigRes[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
	requireBothColumns?: boolean;
}): string[] {
	const candidateName = candidateLabel(params.candidateIndex);
	return maneuverIncompleteItems({ ...params, candidateName }).map((item) => item.reason);
}

/** Maneuver outcome gaps only — used by Step 2 Next and as part of Step 3 core review. */
export function getStep2ManeuverOutcomeIncompleteReasons(params: Step2ManeuverOutcomesValidationParams): string[] {
	if (!params.candidateCount) return ["Add at least one candidate"];

	const hasSecondCandidate = params.candidateCount >= 2;
	const reasons: string[] = [];

	for (let i = 0; i < params.candidateCount; i++) {
		reasons.push(
			...maneuverIncompleteReasons({
				candidateIndex: i,
				crewRoles: params.crewRoles,
				hasSecondCandidate,
				assessmentConfig: params.assessmentConfig,
				maneuverOutcomes: params.maneuverOutcomes,
			}),
		);
	}

	return reasons;
}

export function isStep2ManeuverOutcomesComplete(params: Step2ManeuverOutcomesValidationParams): boolean {
	return getStep2ManeuverOutcomeIncompleteReasons(params).length === 0;
}

/** Core review gaps only (recommendation / Rating C / maneuver outcomes) — excludes OB comments. */
export function getStep3CoreReviewIncompleteItems(params: Step3ReviewValidationParams): Step3ReviewIncompleteItem[] {
	if (!params.candidateCount) {
		return [
			{
				candidateIndex: -1,
				candidateName: "Session",
				section: "outcome",
				field: "candidates",
				label: "Add at least one candidate",
				reason: "Add at least one candidate",
			},
		];
	}

	if (!params.signOffForm?.length) {
		return [
			{
				candidateIndex: -1,
				candidateName: "Session",
				section: "outcome",
				field: "signOffForm",
				label: "Return to Candidate Rating and click Next to open Review, or refresh this step",
				reason: "Return to Candidate Rating and click Next to open Review, or refresh this step",
			},
		];
	}

	const hasSecondCandidate = params.candidateCount >= 2;
	const items: Step3ReviewIncompleteItem[] = [];

	for (let i = 0; i < params.candidateCount; i++) {
		const signOffGroup = params.signOffForm.at(i) as UntypedFormGroup | undefined;
		const competencyBlocks = params.getCompetencyBlocksForIndex(i);
		const candidateName = params.getCandidateNameForIndex?.(i) ?? candidateLabel(i);

		items.push(...recommendationIncompleteItems(signOffGroup, i, candidateName));
		items.push(...reminderStatusIncompleteItems(signOffGroup, i, candidateName));
		items.push(
			...competencyIncompleteItems({
				candidateIndex: i,
				candidateName,
				hasSecondCandidate,
				temCompetencyOutcomes: params.temCompetencyOutcomes,
				competencyBlocks,
			}),
		);
		items.push(
			...maneuverIncompleteItems({
				candidateIndex: i,
				candidateName,
				crewRoles: params.crewRoles,
				hasSecondCandidate,
				assessmentConfig: params.assessmentConfig,
				maneuverOutcomes: params.maneuverOutcomes,
				requireBothColumns: true,
			}),
		);
	}

	return items;
}

/** Group core review gaps by candidate for the Step 3 checklist UI. */
export function groupStep3CoreReviewIncompleteItems(items: Step3ReviewIncompleteItem[]): Step3ReviewIncompleteGroup[] {
	const groups: Step3ReviewIncompleteGroup[] = [];

	for (const item of items) {
		const existing = groups.find((g) => g.candidateIndex === item.candidateIndex);
		if (existing) {
			existing.items.push(item);
		} else {
			groups.push({
				candidateIndex: item.candidateIndex,
				candidateName: item.candidateName,
				items: [item],
			});
		}
	}

	return groups;
}

/** Core review gaps only (recommendation / Rating C / maneuver outcomes) — excludes OB comments. */
export function getStep3CoreReviewIncompleteReasons(params: Step3ReviewValidationParams): string[] {
	return getStep3CoreReviewIncompleteItems(params).map((item) => item.reason);
}

function obCommentIncompleteReasons(params: Step3ReviewValidationParams): string[] {
	const missing = getStep3MissingObComments(params);
	if (!missing.length) return [];

	const counts = new Map<string, number>();
	for (const row of missing) {
		counts.set(row.candidateName, (counts.get(row.candidateName) ?? 0) + 1);
	}

	return Array.from(counts.entries()).map(([name, count]) =>
		count === 1 ? `${name}: 1 OB comment missing` : `${name}: ${count} OB comments missing`,
	);
}

export function isStep3CoreReviewComplete(params: Step3ReviewValidationParams): boolean {
	return getStep3CoreReviewIncompleteReasons(params).length === 0;
}

export function isStep3ReviewComplete(params: Step3ReviewValidationParams): boolean {
	return isStep3CoreReviewComplete(params) && isStep3ObCommentsComplete(params);
}

/** Human-readable list of what still blocks Step 3 → Sign off. */
export function getStep3ReviewIncompleteReasons(params: Step3ReviewValidationParams): string[] {
	return [...getStep3CoreReviewIncompleteReasons(params), ...obCommentIncompleteReasons(params)];
}

/** Re-export for tests that build competency blocks inline. */
export { buildTs1ReviewCompetencyBlocks };
